import { clearAccessToken, useGetMyProfileQuery, useLogoutUserMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { Link, useNavigate } from '@tanstack/react-router'
import { ChevronLeft, Loader2, LogOut } from 'lucide-react'

import { useAuthStore } from '@/stores/auth.store'

import { EmailsSection } from './emails-section'
import { PassengersSection } from './passengers-section'
import { PasswordSection } from './password-section'
import { PersonalDataSection } from './personal-data-section'
import { PhoneNumbersSection } from './phone-numbers-section'

export function PersonalInformationsPage() {
  const { updateAuth } = useAuthStore()
  const navigate = useNavigate()
  const { data: profile, isLoading } = useGetMyProfileQuery()
  const { mutateAsync: logout } = useLogoutUserMutation()

  const handleLogout = async () => {
    try {
      await logout()
    } catch {
      // Logout even if server call fails
    }
    clearAccessToken()
    updateAuth({ isAuthenticated: false, user: undefined })
    void navigate({ to: '/connexion' })
  }

  return (
    <div className="flex min-h-screen flex-col">
      {/* Hero Banner */}
      <section className="bg-brand-600 relative overflow-hidden px-6 py-8 md:py-10">
        <img
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-50"
          src="/images/customer-arear-background.jpg"
        />
        <div className="relative z-10 mx-auto flex w-full max-w-3xl flex-col gap-2">
          <h1 className="text-3xl font-semibold tracking-tight text-white md:text-5xl">
            Mes données
          </h1>
          <p className="text-sm font-medium text-white">Gérez votre compte Massilia Voyages</p>
        </div>
      </section>

      {/* Content Area */}
      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-6 bg-white px-6 py-8 md:py-10">
        {/* Back link */}
        <Link className="text-brand-600 flex items-center gap-1 text-sm font-medium" to="/">
          <ChevronLeft className="size-4" />
          Retour
        </Link>

        {isLoading || !profile ? (
          <div className="flex items-center justify-center py-14">
            <Loader2 className="text-brand-600 size-8 animate-spin" />
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <h2 className="text-gray-light-900 text-xl font-semibold">Données personnelles</h2>

            {/* Personal data */}
            <PersonalDataSection profile={profile} />

            {/* Phone numbers */}
            <PhoneNumbersSection profile={profile} />

            {/* Emails */}
            <EmailsSection profile={profile} />

            {/* Password */}
            <PasswordSection />

            {/* Passengers */}
            <PassengersSection />
          </div>
        )}

        {/* Logout button */}
        <Button
          className="w-fit gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          onClick={() => void handleLogout()}
          variant="outline"
        >
          <LogOut className="size-4" />
          Se déconnecter
        </Button>
      </main>
    </div>
  )
}
